(() => {
  if (customElements.get('bumpino-category-carousel')) return;
  class BumpinoCategoryCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const rail = this.querySelector('[data-category-rail]');
      const group = this.querySelector('[data-category-group]');
      if (!rail || !group?.children.length) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const listen = (event, handler, options = {}) => rail.addEventListener(event, handler, { ...options, signal });
      const clones = Array.from({ length: 2 }, () => {
        const copy = group.cloneNode(true);
        copy.removeAttribute('data-category-group');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a').forEach(link => {
          link.tabIndex = -1;
          link.removeAttribute('data-shopify-editor-block');
        });
        return copy;
      });
      rail.prepend(clones[0]);
      rail.append(clones[1]);
      this.clones = clones;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let width = 0;
      let visible = false;
      let hovered = false;
      let focused = false;
      let drag = null;
      let suppressUntil = 0;
      let resumeAt = 0;
      let last = 0;
      let carry = 0;
      let direction = 1;
      const idle = () => { resumeAt = performance.now() + 3500; };
      const size = () => {
        const nextWidth = group.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
        if (!nextWidth) return;
        const progress = width ? ((rail.scrollLeft % width) + width) % width / width : 0;
        direction = getComputedStyle(group).direction === 'rtl' ? -1 : 1;
        rail.scrollLeft = !width && direction === -1 ? nextWidth * 2 - rail.clientWidth : nextWidth * (1 + progress);
        width = nextWidth;
      };
      const normalize = () => {
        if (width && !drag && !focused) {
          if (rail.scrollLeft >= width * 2) rail.scrollLeft -= width;
          else if (rail.scrollLeft < width) rail.scrollLeft += width;
        }
      };
      this.resizeObserver = new ResizeObserver(size);
      this.resizeObserver.observe(group);
      this.intersectionObserver = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }, { threshold: .05 });
      this.intersectionObserver.observe(this);
      size();
      const animate = time => {
        const elapsed = last ? Math.min(time - last, 80) : 0;
        last = time;
        if (visible && !document.hidden && !motion.matches && !hovered && !focused && !drag && time >= resumeAt) {
          normalize();
          carry += elapsed * .03;
          const distance = Math.floor(carry);
          if (distance) { rail.scrollLeft += distance * direction; carry -= distance; }
        }
        this.frame = requestAnimationFrame(animate);
      };
      listen('pointerenter', event => { if (event.pointerType === 'mouse') hovered = true; });
      listen('pointerleave', () => { hovered = false; idle(); });
      listen('focusin', () => { focused = true; });
      listen('focusout', event => { focused = rail.contains(event.relatedTarget); idle(); });
      listen('wheel', idle, { passive: true });
      listen('touchstart', idle, { passive: true });
      listen('touchmove', idle, { passive: true });
      listen('touchend', idle, { passive: true });
      listen('pointerdown', event => {
        idle();
        if (event.pointerType === 'mouse' && event.button === 0) drag = { id: event.pointerId, startX: event.clientX, lastX: event.clientX, moved: false };
      });
      listen('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (Math.abs(event.clientX - drag.startX) > 6) drag.moved = true;
        if (drag.moved) {
          rail.setPointerCapture(drag.id);
          rail.classList.add('is-dragging');
          rail.scrollLeft += drag.lastX - event.clientX;
          event.preventDefault();
        }
        drag.lastX = event.clientX;
      });
      const finish = () => {
        if (drag?.moved) suppressUntil = performance.now() + 450;
        if (drag && rail.hasPointerCapture(drag.id)) rail.releasePointerCapture(drag.id);
        drag = null;
        rail.classList.remove('is-dragging');
        idle();
      };
      listen('pointerup', finish);
      listen('pointercancel', finish);
      listen('lostpointercapture', finish);
      listen('dragstart', event => event.preventDefault());
      listen('click', event => {
        if (performance.now() < suppressUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true });
      listen('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        idle();
        rail.scrollBy({ left: rail.clientWidth * .7 * (event.key === 'ArrowRight' ? 1 : -1), behavior: motion.matches ? 'auto' : 'smooth' });
      });
      this.frame = requestAnimationFrame(animate);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.controller?.abort();
      this.clones?.forEach(clone => clone.remove());
      this.controller = null;
    }
  }
  customElements.define('bumpino-category-carousel', BumpinoCategoryCarousel);
})();
